/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function sendEventGTMCrossCell(event = false, productTitle = '', type = null) {
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: `${type === 'complementary' ? 'crosssell' : 'upsell'}_add_product`,
    productTitle
  });
}

function handleSwatchChangeCustom(elem) {
  const targetProduct = elem.closest('.product-vx');

  const variantId = elem.dataset.variantId;
  if (variantId) {
    const productBtnAddToCart = targetProduct.querySelector('.product-to-cart.add-product-to-cart-custom');
    productBtnAddToCart.setAttribute('data-product-id', variantId);
  }


  const mediaId = elem.dataset.mediaId;
  if (mediaId) {
    const targetProductImage = elem.closest('.product-image');

    const productImageHeight = targetProductImage.getBoundingClientRect().height;
    targetProductImage.style.height = `${productImageHeight}px`;

    const mediaOldSelected = targetProductImage.querySelector('.image-primary .img:not([hidden])');
    const mediaNewSelected = targetProductImage.querySelector(`.image-primary .img[data-media-id="${mediaId}"]`);

    mediaOldSelected.hidden = true;
    mediaNewSelected.hidden = false;

    setTimeout(() => {
      targetProductImage.removeAttribute('style');
    }, 2000)
  }

  const price = parseFloat(elem.dataset.price, 16);
  if (price) {
    const productPriceDefault = targetProduct.querySelector('.product-price .product-price-default');

    const newPrice = (price / 100).toLocaleString(
      'pt-br',
      {
        style: 'currency', 
        currency: 'BRL'
      }
    );

    productPriceDefault.textContent = newPrice;

    const compareAtPrice = parseFloat(elem.dataset.compareAtPrice, 16);
    if (compareAtPrice) {
      const productPriceWas = targetProduct.querySelector('.product-price .product-price-was');

      if (compareAtPrice <= price) {
        productPriceWas.innerHTML = 'Por apenas';
      } else if (compareAtPrice > price) {
        const newCompareAtPrice = (compareAtPrice / 100).toLocaleString(
          'pt-br',
          {
            style: 'currency', 
            currency: 'BRL'
          }
        );

        productPriceWas.innerHTML = `<span class="promo">${newCompareAtPrice}</span>`;
      }
    }
  }
}

async function addProductToCartCustom(variantId, target) {
  try {
    target.classList.add('show');
    target.disabled = true;

    const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
    if (!oldCartResponse.ok) {
      throw new Error(oldCartResponse.status);
    }

    const oldCartData = await oldCartResponse.json();

    const data = await fetch(`${theme.routes.cartAdd}.js`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      }),
    }).then(response => response.json());

    document.dispatchEvent(new Event('dispatch:cart-drawer:refresh'));

    const newCartResponse = await fetch(`${theme.routes.cart}.js`);
    if (!newCartResponse.ok) {
      throw new Error(newCartResponse.status);
    }

    const newCartData = await newCartResponse.json();

    const itemInOldCart = oldCartData.items.find(item => 
      item.variant_id === data.variant_id
    );

    if (itemInOldCart) {
      document.dispatchEvent(new CustomEvent('on:line-item:change', {
        bubbles: true,
        detail: {
          cart: newCartData,
          variantId: data.variant_id,
          oldQuantity: itemInOldCart.quantity,
          newQuantity: (itemInOldCart.quantity === data.quantity)
            ? itemInOldCart.quantity : data.quantity
        }
      }));
    } else {
      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          cart: newCartData,
          variantId: data.variant_id
        }
      }));
    }

    document.dispatchEvent(new Event('dispatch:cart-drawer:open'));
  } catch (error) {
    console.log(error);

    this.dispatchEvent(new CustomEvent('on:cart:error', {
      bubbles: true,
      detail: {
        error: error.message
      }
    }));
  } finally {
    target.classList.remove('show');
    target.disabled = false;
  }
}

document.querySelectorAll('.products-grid-container .card--product form.js-product-form button[name="add"]').forEach(item => {
  item.addEventListener('click', () => {
    window._conv_q = window._conv_q || [];
    _conv_q.push(["triggerConversion", "100494515"]);
  });
});

document.querySelectorAll('.link-list-categories-metrics').forEach(item => {
  item.addEventListener('click', (event) => {
    event.preventDefault();

    window._conv_q = window._conv_q || [];
    _conv_q.push(["triggerConversion", "100494517"]);

    location.href = item.href;
  });
});

document.querySelectorAll('.banners-square-slides .click-in-banner-square-metrics').forEach(item => {
  item.addEventListener('click', (event) => {
    event.preventDefault();

    window._conv_q = window._conv_q || [];
    _conv_q.push(["triggerConversion", "100494519"]);

    _conv_q.push(["triggerConversion", "100494783"]);

    location.href = item.href;
  });
});

document.querySelectorAll('.container-story .stories .story .product-buy a.product-link').forEach(item => {
  item.addEventListener('click', (event) => {
    event.preventDefault();

    window._conv_q = window._conv_q || [];
    _conv_q.push(["triggerConversion", "100494783"]);

    location.href = item.href;
  });
});

function handleClickAddProduct(target) {
  window._conv_q = window._conv_q || [];
  _conv_q.push(["triggerConversion", "100494515"]);

  const variantId = target.dataset.productId;

  addProductToCartCustom(variantId, target)
}

function redirectToWhatsApp(elem) {
  window.open(elem.dataset.linkChat, '_blank').focus();
}

async function generateLinksharedCart() {
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'cart_share_affiliate_link',
  });
  
  const btnLinkVendedorCart = document.querySelector('.vx-container-link-vendedor-cart button');
  const textBoxCart = btnLinkVendedorCart.querySelector('.text-box');
  const copyBoxCart = document.querySelector('.vx-container-link-vendedor-cart .copy-box');
  
  const code = 'VX' + (btnLinkVendedorCart.dataset.codigoVendedor).padStart(4, 0);
  const productList = btnLinkVendedorCart.dataset.productList;

  const hasFreeShipping = document.querySelector('.control-checkbox input[name="hasStorePickup"]:checked');
  
  let listDiscount = code;

  if (hasFreeShipping) {
    listDiscount = listDiscount + ',retirenaloja'
  }

  //const longUrl = `${location.origin}/discount/${code}?redirect=/cart/${productList}?storefront=true&utm_medium=indicacao&utm_source=consultor&utm_campaign=copy&utm_term=carrinho&utm_content=${code}`;
  
  const longUrl = `${location.origin}/cart/${productList}?discount=${listDiscount}&utm_medium=indicacao&utm_source=consultor&utm_campaign=copy&utm_term=carrinho&utm_content=${code}`;

  try {
    btnLinkVendedorCart.disabled = true;
    btnLinkVendedorCart.classList.add('execute');
    textBoxCart.classList.add('hidden');

    const result = await fetch(
      `https://integrations.vxcaseloja.com/api/affiliate-link`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'x-api-key': 'nn9zEodFdZ9xJtNPjiCxy4WdrUX97WfY9PnK5aQ1'
        },
        body: JSON.stringify({
          longUrl,
          title: `Compartilhamento de carrinho do vendedor ${code}`
        })
      },
    ).then(response => response.json());

    if (!result.shortUrl) {
      throw new Error('Nao foi possivel obter o link short');
    }
    
    btnLinkVendedorCart.classList.remove('execute');
    btnLinkVendedorCart.classList.add('generated');
    textBoxCart.querySelector('.text-main').classList.add('hidden');
    textBoxCart.querySelector('.text-secondary').classList.remove('hidden');
    textBoxCart.classList.remove('hidden');

    copyBoxCart.querySelector('input').value = result.shortUrl;
    copyBoxCart.classList.add('show');
  } catch (error) {
    btnLinkVendedorCart.classList.remove('execute');
    btnLinkVendedorCart.classList.add('generated');
    textBoxCart.querySelector('.text-main').classList.add('hidden');
    textBoxCart.querySelector('.text-secondary').classList.remove('hidden');
    textBoxCart.classList.remove('hidden');

    copyBoxCart.querySelector('input').value = longUrl;
    copyBoxCart.classList.add('show');
    
    console.error("Falha ao copiar texto: ", error);
  }
}

async function copyToClipboardCart() {
  const textToCopyValue = document.querySelector('.vx-container-link-vendedor-cart .copy-box input').value;
  const copyTooltip = document.querySelector('.vx-container-link-vendedor-cart .copy-box button .copy-tooltip');

  try {
    await navigator.clipboard.writeText(textToCopyValue);
    copyTooltip.classList.add('show');

    setTimeout(() => {
      copyTooltip.classList.remove('show');
    }, 3000);
  } catch (error) {
    console.error("Falha ao copiar texto: ", error);
  }
}

async function generateLinkSharedCart2() {
  const dataLayer = window.dataLayer || [];
  dataLayer.push({
    event: 'cart_share_affiliate_link',
  });
  
  const btnLinkVendedorCart = document.querySelector('.vx-container-link-vendedor-cart button');
  const textBoxCart = btnLinkVendedorCart.querySelector('.text-box');
  const copyBoxCart = document.querySelector('.vx-container-link-vendedor-cart .copy-box');
  
  const code = 'VX' + (btnLinkVendedorCart.dataset.codigoVendedor).padStart(4, 0);
  const getProducts = await fetch(`https://${window.location.host}/cart.js`).then(res => res.json());
  const productsList = [];
  getProducts.items.forEach(product => {
    productsList.push(`${product.variant_id}:${product.quantity}`)
  })

  const hasFreeShipping = document.querySelector('.control-checkbox input[name="hasStorePickup"]:checked');
  
  let listDiscount = code;

  if (hasFreeShipping) {
    listDiscount = listDiscount + ',retirenaloja'
  }

  //const longUrl = `${location.origin}/discount/${code}?redirect=/cart/${productList}?storefront=true&utm_medium=indicacao&utm_source=consultor&utm_campaign=copy&utm_term=carrinho&utm_content=${code}`;
  
  const longUrl = `${location.origin}/cart/${productsList.join(',')}?discount=${listDiscount}&utm_medium=indicacao&utm_source=consultor&utm_campaign=copy&utm_term=carrinho&utm_content=${code}`;

  try {
    btnLinkVendedorCart.disabled = true;
    btnLinkVendedorCart.classList.add('execute');
    textBoxCart.classList.add('hidden');

    const result = await fetch(
      `https://integrations.vxcaseloja.com/api/affiliate-link`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'x-api-key': 'nn9zEodFdZ9xJtNPjiCxy4WdrUX97WfY9PnK5aQ1'
        },
        body: JSON.stringify({
          longUrl,
          title: `Compartilhamento de carrinho do vendedor ${code}`
        })
      },
    ).then(response => response.json());

    if (!result.shortUrl) {
      throw new Error('Nao foi possivel obter o link short');
    }
    
    btnLinkVendedorCart.classList.remove('execute');
    btnLinkVendedorCart.classList.add('generated');
    textBoxCart.querySelector('.text-main').classList.add('hidden');
    textBoxCart.querySelector('.text-secondary').classList.remove('hidden');
    textBoxCart.classList.remove('hidden');

    copyBoxCart.querySelector('input').value = result.shortUrl;
    copyBoxCart.classList.add('show');
  } catch (error) {
    btnLinkVendedorCart.classList.remove('execute');
    btnLinkVendedorCart.classList.add('generated');
    textBoxCart.querySelector('.text-main').classList.add('hidden');
    textBoxCart.querySelector('.text-secondary').classList.remove('hidden');
    textBoxCart.classList.remove('hidden');

    copyBoxCart.querySelector('input').value = longUrl;
    copyBoxCart.classList.add('show');
    
    console.error("Falha ao copiar texto: ", error);
  }
}